import { Play } from "lucide-react";

import type { TurnEntry } from "../../api/calls";

/**
 * Interactive turn timeline. Each T-segment is a button — clicking it
 * jumps the Recording player to that turn's offset in the audio.
 *
 * Offset is approximated as `turn.started_at - callStartedAt`. The real
 * t=0 of the recording is slightly later (egress startup latency, ~hundreds
 * of ms), so the jump lands a beat before the turn — operators can nudge
 * forward with the audio control if needed.
 *
 * Disabled for LIVE calls — there's no recording to seek into yet.
 */
export function AudioScrubber({
  turns,
  callStartedAt,
  onJumpToTurn,
  disabled = false,
}: {
  turns: TurnEntry[];
  callStartedAt: string;
  onJumpToTurn: (offsetSeconds: number) => void;
  disabled?: boolean;
}) {
  if (turns.length === 0) {
    return null;
  }
  const callStartMs = Date.parse(callStartedAt);
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        gap: 14,
        alignItems: "center",
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-card)",
        padding: "12px 16px",
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          border: "1px solid var(--accent-line)",
          display: "grid",
          placeItems: "center",
          color: "var(--accent)",
          background: "var(--accent-subtle)",
        }}
        title={disabled ? "Recording not available while LIVE" : "Click a turn to jump"}
      >
        <Play size={11} strokeWidth={1.75} fill="currentColor" />
      </div>

      <div
        style={{
          height: 28,
          background: "var(--bg-tertiary)",
          border: "1px solid var(--border-subtle)",
          display: "grid",
          gridTemplateColumns: `repeat(${turns.length}, minmax(28px, 1fr))`,
          overflowX: "auto",
          overflowY: "hidden",
        }}
      >
        {turns.map((t, i) => {
          const hasErrors = (t.errors ?? []).length > 0;
          const offsetMs = Date.parse(t.started_at) - callStartMs;
          const offsetSeconds = Number.isFinite(offsetMs) ? Math.max(0, offsetMs / 1000) : 0;
          return (
            <button
              key={t.turn_id}
              type="button"
              disabled={disabled}
              title={
                disabled
                  ? `T${t.turn_index}${hasErrors ? " · errors" : ""}`
                  : `Jump to T${t.turn_index} (+${formatOffset(offsetSeconds)})${hasErrors ? " · errors" : ""}`
              }
              onClick={() => onJumpToTurn(offsetSeconds)}
              style={{
                appearance: "none",
                border: "none",
                padding: 0,
                cursor: disabled ? "default" : "pointer",
                borderRight:
                  i === turns.length - 1
                    ? "none"
                    : "1px solid var(--border-subtle)",
                background: hasErrors
                  ? "rgba(244,63,94,0.15)"
                  : "rgba(94,234,212,0.10)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "var(--font-mono)",
                fontSize: 9.5,
                letterSpacing: "0.10em",
                color: hasErrors ? "var(--error)" : "var(--accent)",
                fontWeight: 500,
                transition: "background 80ms",
              }}
              onMouseEnter={(e) => {
                if (disabled) return;
                e.currentTarget.style.background = hasErrors
                  ? "rgba(244,63,94,0.30)"
                  : "rgba(94,234,212,0.25)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = hasErrors
                  ? "rgba(244,63,94,0.15)"
                  : "rgba(94,234,212,0.10)";
              }}
            >
              T{String(t.turn_index).padStart(2, "0")}
            </button>
          );
        })}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 2,
          minWidth: 70,
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9.5,
            letterSpacing: "0.16em",
            color: "var(--text-muted)",
            textTransform: "uppercase",
          }}
        >
          TURNS
        </span>
        <span
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 18,
            fontWeight: 600,
            color: "var(--text-primary)",
            letterSpacing: "-0.01em",
            lineHeight: 1,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {turns.length}
        </span>
      </div>
    </div>
  );
}

function formatOffset(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}
